import { X } from 'lucide-react';
import { useState } from 'react';
import { useUpdateKycStatus } from '../hooks/useUpdateKycStatus';

const KycManagementModal = ({ isOpen, onClose, customer }) => {
  const [status, setStatus] = useState(customer?.kyc?.status || '');
  const [note, setNote] = useState('');
  const { mutate: updateKycStatus, isPending } = useUpdateKycStatus();

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    updateKycStatus(
      {
        customerId: customer._id,
        status,
        note: note || undefined,
      },
      {
        onSuccess: () => {
          onClose();
          setNote('');
        },
      }
    );
  };

  const statusOptions = [
    { value: 'pending', label: 'Pending', description: 'KYC documents pending submission' },
    { value: 'under_review', label: 'Under Review', description: 'KYC is being reviewed' },
    { value: 'approved', label: 'Approved', description: 'KYC approved and verified' },
    { value: 'rejected', label: 'Rejected', description: 'KYC rejected - needs resubmission' },
    { value: 'expired', label: 'Expired', description: 'KYC documents expired' },
  ];

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm'>
      <div className='bg-card border border-border rounded-lg shadow-lg w-full max-w-md mx-4'>
        {/* Header */}
        <div className='flex items-center justify-between p-6 border-b border-border'>
          <h2 className='text-lg font-semibold text-foreground'>Manage KYC Status</h2>
          <button
            onClick={onClose}
            className='text-muted-foreground hover:text-foreground transition-colors'
            disabled={isPending}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className='p-6 space-y-4'>
          <div>
            <label className='block text-sm font-medium text-foreground mb-2'>Select KYC Status</label>
            <div className='space-y-2'>
              {statusOptions.map((option) => (
                <label
                  key={option.value}
                  className='flex items-start p-3 border border-border rounded-md cursor-pointer hover:bg-accent transition-colors'
                >
                  <input
                    type='radio'
                    name='status'
                    value={option.value}
                    checked={status === option.value}
                    onChange={(e) => setStatus(e.target.value)}
                    className='mt-1'
                    required
                  />
                  <div className='ml-3'>
                    <div className='text-sm font-medium text-foreground capitalize'>{option.label}</div>
                    <div className='text-xs text-muted-foreground'>{option.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor='note' className='block text-sm font-medium text-foreground mb-2'>
              Note (Optional)
            </label>
            <textarea
              id='note'
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary'
              placeholder='Add any additional notes...'
            />
          </div>

          {/* Footer */}
          <div className='flex justify-end gap-3 pt-4'>
            <button
              type='button'
              onClick={onClose}
              className='px-4 py-2 text-sm border border-border rounded-md bg-card hover:bg-accent transition-colors'
              disabled={isPending}
            >
              Cancel
            </button>
            <button
              type='submit'
              className='px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50'
              disabled={isPending}
            >
              {isPending ? 'Updating...' : 'Update KYC Status'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default KycManagementModal;
